// Stop a mis-tap throwing away a run that is still going.
//
// Endurance is the headline mode and its score is a TIME, so the worst thing
// the app can do is lose four minutes of it to a thumb landing on the wrong
// tab. Endurance and the Daily are both TAB ROOTS, which makes this worse
// rather than better: there is no back button to be careful with, just three
// other tabs one tap away along the bottom edge, exactly where a thumb rests.
//
// So: while a run is in progress, leaving asks first. When it is not, this is
// completely inert - no dialog on the way out of a screen he has finished
// with, which would be its own kind of annoying.
//
// It deliberately does NOT try to save the abandoned run. A run he walked out
// of is not a score he set, and quietly recording one would put a number in
// the trophy room he did not earn.

import { onUnmounted } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import { alertController, onIonViewDidLeave, onIonViewWillEnter } from '@ionic/vue';

/**
 * Stop everything this screen has running, whenever it stops being on screen.
 *
 * `onUnmounted` is NOT enough, and on the tab roots it never runs at all:
 * IonRouterOutlet keeps a tab's page alive when you switch away from it, so
 * Endurance carried on ticking in the background — spending a life per timeout
 * until the run "ended" on its own and yanked the player to Results from
 * whatever screen they had moved to. That is two bugs (a run that will not
 * stop, and navigation that breaks afterwards) from one missing teardown.
 *
 * `onIonViewDidLeave` is the one that fires in every case: tab switch, back,
 * and replace alike. `onUnmounted` stays as the belt to its braces.
 *
 * @param {() => void} stop  idempotent; called every time the view leaves
 */
export function useStopOnLeave(stop) {
  onIonViewDidLeave(() => stop());
  onUnmounted(() => stop());
}

/**
 * Put this screen back to its pre-game state every time it comes on screen.
 *
 * The same outlet caching that kept Endurance ticking also means "Go again"
 * lands on the SAME component instance, still holding the finished run: its
 * `ended` flag still true, its queue still exhausted, its last verdict still
 * on screen. The player pressed a button called Go again and nothing went
 * again.
 *
 * @param {() => void} reset  idempotent; called before each appearance
 */
export function useResetOnEnter(reset) {
  onIonViewWillEnter(() => reset());
}

/**
 * @param {import('vue').Ref<boolean>} isRunning  true while a run is live
 * @param {object} [copy]
 * @param {() => void} [copy.onAbandon]  END the run when the player confirms.
 *   Without this the dialog only permits the navigation: the run itself keeps
 *   going, which is precisely what "Give up" is supposed to prevent.
 */
export function useAbandonGuard(isRunning, copy = {}) {
  const {
    header = 'Give up?',
    message = 'You are in the middle of a run. Leaving now loses this score.',
    confirmText = 'Give up',
    cancelText = 'Keep going',
  } = copy;

  onBeforeRouteLeave(async () => {
    if (!isRunning.value) return true;

    const alert = await alertController.create({
      header,
      message,
      // Cancel first, and it is the one a stray tap is most likely to hit.
      // The destructive option should never be the easy one to reach.
      buttons: [
        { text: cancelText, role: 'cancel' },
        { text: confirmText, role: 'destructive' },
      ],
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    const leaving = role === 'destructive';
    if (leaving) copy.onAbandon?.();
    return leaving;
  });
}
